import React from 'react'
import { IonCol, IonGrid, IonItem, IonItemDivider, IonLabel, IonRow } from '@ionic/react'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import {
  saveVitals,
  setEyeOpeningResponse,
  setEyeOpeningResponsePNNV,
  setEyeOpeningResponseScore,
  setMotorResponse,
  setMotorResponsePNNV,
  setMotorResponseScore,
  setQualifier,
  setQualifierPNNV,
  setTotalCGSScore,
  setTotalCGSScorePNNV,
  setVerbalResponse,
  setVerbalResponsePNNV,
  setVerbalResponseScore,
} from '../store/vitals.actions'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IRootState } from '../../../store'
import './GlasgowComaScale.component.css'
import { getAllPNNVOptionsSelector, getVitalOptionsSelector } from '../../options/selectors'
import { IOption, IPNNVOption } from '../../../common/common.types'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import { getGCSOptionsSelector } from '../../options/selectors/gcsOptions.selector'

const GlasgowComaScaleComponent: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const vitals = useSelector((state: IRootState) => state.vitals)

  const {
    eyeOpeningResponse,
    eyeOpeningResponsePNNV,
    verbalResponse,
    verbalResponsePNNV,
    motorResponse,
    motorResponsePNNV,
    totalGCSScore,
    totalGCSScorePNNV,
    gcsQualifier,
    gcsQualifierPNNV,
  } = vitals

  const vitalOptions = useSelector(getVitalOptionsSelector)
  const { motorResponseOptions, verbalResponseOptions, eyeOpeningResponseOptions } = vitalOptions

  const gcsOptionsData = useSelector(getGCSOptionsSelector)
  const { gcsQualifierOptions } = gcsOptionsData

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const gcsTotalNegativeOptions = allPNNVOptions?.['eVitals.23']

  const eyeOpeningResponseOnChange = (option: IOption) => {
    dispatch(setEyeOpeningResponseScore(option !== null ? option?.id : 0))
    dispatch(setEyeOpeningResponse(option))
    dispatch(saveVitals())
  }

  const eyeOpeningResponsePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setEyeOpeningResponsePNNV(option))
    checkTotalCGCSScorePNNV(option)
    dispatch(saveVitals())
  }

  const verbalResponseOnChange = (option: IOption) => {
    dispatch(setVerbalResponseScore(option !== null ? option?.id : 0))
    dispatch(setVerbalResponse(option))
    dispatch(saveVitals())
  }

  const verbalResponsePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setVerbalResponsePNNV(option))
    checkTotalCGCSScorePNNV(option)
    dispatch(saveVitals())
  }

  const motorResponseOnChange = (option: IOption) => {
    dispatch(setMotorResponseScore(option !== null ? option?.id : 0))
    dispatch(setMotorResponse(option))
    dispatch(saveVitals())
  }

  const motorResponsePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setMotorResponsePNNV(option))
    checkTotalCGCSScorePNNV(option)
    dispatch(saveVitals())
  }

  const checkTotalCGCSScorePNNV = (option: IPNNVOption | null) => {
    if (!totalGCSScorePNNV) {
      totalGCSScorePNNVOnChange(option)
    }
  }

  const totalGCSScorePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setTotalCGSScorePNNV(option))
    dispatch(setTotalCGSScore(null))
    dispatch(saveVitals())
  }

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('vitals.gcs.header')}
        </IonLabel>
      </IonItemDivider>

      <IonGrid className='ion-no-padding'>
        <IonRow class='ion-align-items-baseline'>
          <IonCol className={eyeOpeningResponsePNNV ? '' : 'gcs-response-column'}>
            <SingleSelect
              dataTestID='EyeOpeningResponse'
              options={eyeOpeningResponseOptions}
              selectedOption={eyeOpeningResponse}
              listHeader={t('vitals.gcs.form.EyeOpeningResponse')}
              label={t('vitals.gcs.form.EyeOpeningResponse')}
              action={eyeOpeningResponseOnChange}
              pnnvAction={eyeOpeningResponsePNNVOnChange}
              selectedPNNVOption={eyeOpeningResponsePNNV}
              showSelectedOptionId='prefixScore'
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow class='ion-align-items-baseline'>
          <IonCol className={verbalResponsePNNV ? '' : 'gcs-response-column'}>
            <SingleSelect
              dataTestID='VerbalResponse'
              options={verbalResponseOptions}
              selectedOption={verbalResponse}
              listHeader={t('vitals.gcs.form.VerbalResponse')}
              label={t('vitals.gcs.form.VerbalResponse')}
              action={verbalResponseOnChange}
              pnnvAction={verbalResponsePNNVOnChange}
              selectedPNNVOption={verbalResponsePNNV}
              showSelectedOptionId='prefixScore'
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow class='ion-align-items-baseline'>
          <IonCol className={motorResponsePNNV ? '' : 'gcs-response-column'}>
            <SingleSelect
              dataTestID='MotorResponse'
              options={motorResponseOptions}
              selectedOption={motorResponse}
              listHeader={t('vitals.gcs.form.MotorResponse')}
              label={t('vitals.gcs.form.MotorResponse')}
              action={motorResponseOnChange}
              pnnvAction={motorResponsePNNVOnChange}
              selectedPNNVOption={motorResponsePNNV}
              showSelectedOptionId='prefixScore'
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow class='ion-align-items-baseline'>
          <IonCol sizeXs={totalGCSScorePNNV ? '12' : '8'}>
            <SingleSelect
              dataTestID={t('vitals.gcs.form.GCSTotal')}
              options={gcsTotalNegativeOptions}
              selectedPNNVOption={totalGCSScorePNNV}
              listHeader={t('vitals.gcs.form.GCSTotal')}
              label={t('vitals.gcs.form.GCSTotal')}
              action={() => {
                //
              }}
              pnnvAction={totalGCSScorePNNVOnChange}
              showPNNVAsNormals={true}
            />
          </IonCol>
          {!totalGCSScorePNNV && (
            <IonCol>
              <IonItem lines='none'>
                <IonLabel className='ion-text-end gcs-total-label'>
                  {t('vitals.gcs.form.Total')} {totalGCSScore ? totalGCSScore : 'N/A'}
                </IonLabel>
              </IonItem>
            </IonCol>
          )}
        </IonRow>
        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist ion-no-margin ' size='12'>
            <MultiSelect
              dataTestID={t('vitals.gcs.form.PrGcsQualifier')}
              listHeader={t('vitals.gcs.form.PrGcsQualifier')}
              options={gcsQualifierOptions}
              savedValues={gcsQualifier}
              action={setQualifier}
              label={t('vitals.gcs.form.PrGcsQualifier')}
              showChips={true}
              savedPNNVValue={gcsQualifierPNNV}
              pnnvAction={setQualifierPNNV}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </>
  )
}

export default GlasgowComaScaleComponent
